import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BIBLE_BOOKS } from '../../utils/bibleData';

const OT_IDS = new Set([
  'genesis','exodus','leviticus','numbers','deuteronomy','joshua','judges','ruth',
  '1-samuel','2-samuel','1-kings','2-kings','1-chronicles','2-chronicles','ezra',
  'nehemiah','esther','job','psalms','proverbs','ecclesiastes','song-of-solomon',
  'isaiah','jeremiah','lamentations','ezekiel','daniel','hosea','joel','amos',
  'obadiah','jonah','micah','nahum','habakkuk','zephaniah','haggai','zechariah','malachi',
]);

const PassageSelector = ({
  showPassageSelector,
  selectedBook,
  selectedChapter,
  startVerse,
  endVerse,
  availableChapters,
  availableVerses,
  recordedPassages = [],
  onSetSelectedBook,
  onSetSelectedChapter,
  onSetVerseRange,
  onClose,
}) => {
  const [showRecordedOnly, setShowRecordedOnly] = useState(false);
  // OFF (default): tapping a chapter loads it immediately (whole chapter).
  // ON: verse range inputs + Select button appear, nothing loads until Select.
  const [selectVerses, setSelectVerses] = useState(false);
  const bookListRef = useRef(null);

  // Raw text for the verse range inputs, decoupled from the committed
  // startVerse/endVerse while typing — a controlled input bound directly to
  // a clamped numeric value can never be fully cleared, since React snaps
  // the DOM value back on every keystroke that doesn't parse. Clamping only
  // happens on blur, once the user is done typing.
  const [startText, setStartText] = useState(String(startVerse || 1));
  const [endText, setEndText] = useState(String(endVerse || 1));

  useEffect(() => {
    setStartText(String(startVerse || 1));
  }, [startVerse]);

  useEffect(() => {
    setEndText(String(endVerse || 1));
  }, [endVerse]);

  // Auto-set verse range when chapter changes
  useEffect(() => {
    if (availableVerses && availableVerses.length > 0) {
      const lastVerse = availableVerses[availableVerses.length - 1];
      onSetVerseRange(1, lastVerse);
    }
  }, [availableVerses, onSetVerseRange]);

  // Scroll the selected book into view when the selector opens
  useEffect(() => {
    if (!showPassageSelector || !bookListRef.current) return;
    const el = bookListRef.current.querySelector('[data-selected="true"]');
    if (el) el.scrollIntoView({ block: 'center', behavior: 'instant' });
  }, [showPassageSelector, selectedBook]);

  // Lock body scroll while this full-screen overlay is open. Without this,
  // iOS Safari's keyboard (opened by the verse number inputs) can scroll the
  // page underneath to keep the focused input visible, dragging the bottom
  // nav up through this "fixed" overlay.
  useEffect(() => {
    if (!showPassageSelector) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showPassageSelector]);

  // Build recorded-only indexes
  const recordedIndex = useMemo(() => {
    const byBook = new Map();
    const byChapter = new Map();
    recordedPassages?.forEach(p => {
      if (!byBook.has(p.book)) byBook.set(p.book, new Set());
      byBook.get(p.book).add(p.chapter);
      const key = `${p.book}_${p.chapter}`;
      if (!byChapter.has(key)) byChapter.set(key, new Set());
      if (p.verse) byChapter.get(key).add(p.verse);
    });
    return { byBook, byChapter };
  }, [recordedPassages]);

  const bookOptions = useMemo(() => {
    if (!showRecordedOnly) return BIBLE_BOOKS;
    return BIBLE_BOOKS.filter(b => recordedIndex.byBook.has(b.id));
  }, [showRecordedOnly, recordedIndex.byBook]);

  const chapterOptions = useMemo(() => {
    if (!showRecordedOnly) return availableChapters;
    const set = recordedIndex.byBook.get(selectedBook);
    if (!set) return [];
    return Array.from(set).sort((a, b) => a - b);
  }, [showRecordedOnly, selectedBook, availableChapters, recordedIndex.byBook]);

  const verseOptions = useMemo(() => {
    if (!showRecordedOnly) return availableVerses;
    const key = `${selectedBook}_${selectedChapter}`;
    const set = recordedIndex.byChapter.get(key);
    if (!set) return [];
    return Array.from(set).sort((a, b) => a - b);
  }, [showRecordedOnly, selectedBook, selectedChapter, availableVerses, recordedIndex.byChapter]);

  // Keep selections valid when toggling recorded-only
  useEffect(() => {
    if (!showRecordedOnly) return;
    if (!bookOptions.find(b => b.id === selectedBook)) {
      const next = bookOptions[0]?.id;
      if (next) onSetSelectedBook(next);
      return;
    }
    if (!chapterOptions.includes(selectedChapter)) {
      onSetSelectedChapter(chapterOptions[0] || 1);
      return;
    }
    if (verseOptions.length > 0) {
      const first = verseOptions[0];
      const last = verseOptions[verseOptions.length - 1];
      onSetVerseRange(
        verseOptions.includes(startVerse) ? startVerse : first,
        verseOptions.includes(endVerse) ? endVerse : last,
      );
    }
  }, [showRecordedOnly, bookOptions, chapterOptions, verseOptions, selectedBook, selectedChapter, startVerse, endVerse, onSetSelectedBook, onSetSelectedChapter, onSetVerseRange]);

  if (!showPassageSelector) return null;

  const otBooks = bookOptions.filter(b => OT_IDS.has(b.id));
  const ntBooks = bookOptions.filter(b => !OT_IDS.has(b.id));
  const selectedBookName = BIBLE_BOOKS.find(b => b.id === selectedBook)?.name || '';
  const lastVerse = (showRecordedOnly ? verseOptions : availableVerses)?.[
    (showRecordedOnly ? verseOptions : availableVerses)?.length - 1
  ] || 1;

  function renderBookGroup(label, books) {
    if (books.length === 0) return null;
    return (
      <div key={label}>
        <div className="px-3 pt-3 pb-1.5 text-[10px] font-bold tracking-widest text-white/30 uppercase sticky top-0 bg-[#0d1520]">
          {label}
        </div>
        {books.map(book => {
          const isSelected = book.id === selectedBook;
          const hasRecordings = recordedIndex.byBook.has(book.id);
          return (
            <button
              key={book.id}
              data-selected={isSelected}
              onClick={() => onSetSelectedBook(book.id)}
              className={`w-full text-left px-3 py-2.5 text-sm transition-colors border-l-2 ${
                isSelected
                  ? 'bg-cerulean/20 text-cerulean border-cerulean font-semibold'
                  : 'text-white/75 hover:bg-white/5 border-transparent'
              }`}
            >
              <span className="flex items-center justify-between gap-1">
                <span className="truncate">{book.name}</span>
                {hasRecordings && (
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-cerulean/60" />
                )}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[10000] bg-[#0d1520] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
        <div>
          <h2 className="text-white font-semibold text-base leading-tight">Select Passage</h2>
          <p className="text-white/40 text-xs mt-0.5">
            {selectedBookName} {selectedChapter}
            {startVerse && endVerse && (startVerse !== endVerse ? `:${startVerse}–${endVerse}` : `:${startVerse}`)}
          </p>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 active:scale-95 transition-all"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Toggles: recorded-only filter + verse-selection mode */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium ${!showRecordedOnly ? 'text-white' : 'text-white/40'}`}>All</span>
          <button
            onClick={() => setShowRecordedOnly(v => !v)}
            className={`relative inline-flex h-5 w-9 rounded-full border-2 border-transparent transition-colors duration-200 ${showRecordedOnly ? 'bg-cerulean' : 'bg-white/20'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${showRecordedOnly ? 'translate-x-4' : 'translate-x-0'}`} />
          </button>
          <span className={`text-xs font-medium ${showRecordedOnly ? 'text-cerulean' : 'text-white/40'}`}>Recorded Only</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium ${selectVerses ? 'text-cerulean' : 'text-white/40'}`}>Select Verses</span>
          <button
            onClick={() => setSelectVerses(v => {
              // Leaving verse mode: reset to the whole chapter.
              if (v) onSetVerseRange(1, lastVerse);
              return !v;
            })}
            className={`relative inline-flex h-5 w-9 rounded-full border-2 border-transparent transition-colors duration-200 ${selectVerses ? 'bg-cerulean' : 'bg-white/20'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${selectVerses ? 'translate-x-4' : 'translate-x-0'}`} />
          </button>
        </div>
      </div>

      {/* Two-panel picker */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Book list */}
        <div ref={bookListRef} className="w-[42%] overflow-y-auto border-r border-white/10 bg-[#0d1520]">
          {renderBookGroup('Old Testament', otBooks)}
          {renderBookGroup('New Testament', ntBooks)}
        </div>

        {/* Right: Chapter grid */}
        <div className="flex-1 overflow-y-auto p-3 bg-[#111827]">
          {showRecordedOnly && chapterOptions.length === 0 && (
            <p className="mt-4 text-center text-sm text-white/30">No recordings in this book yet.</p>
          )}
          {/* Fixed-size chapter tiles so they don't balloon on wide viewports */}
          <div className="grid grid-cols-[repeat(auto-fill,3rem)] gap-2 justify-center sm:justify-start">
            {chapterOptions.map(ch => {
              const isSelected = ch === selectedChapter;
              const hasRec = recordedIndex.byBook.get(selectedBook)?.has(ch);
              return (
                <button
                  key={ch}
                  onClick={() => {
                    onSetSelectedChapter(ch);
                    // Default mode: load the chapter immediately on tap.
                    if (!selectVerses) onClose();
                  }}
                  className={`relative w-12 h-12 rounded-xl flex items-center justify-center text-sm font-semibold transition-all active:scale-90 ${
                    isSelected
                      ? 'bg-cerulean text-white shadow-lg shadow-cerulean/30'
                      : 'bg-white/10 text-white/75 hover:bg-white/20'
                  }`}
                >
                  {ch}
                  {hasRec && !isSelected && (
                    <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-cerulean/70" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer: verse range + select (verse-selection mode only) */}
      {selectVerses && (
      <div className="flex-shrink-0 border-t border-white/10 bg-[#0d1520] px-4 pt-3 pb-4" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1 space-y-1">
            <label className="text-xs text-white/40 font-medium">Start verse</label>
            <input
              type="number"
              min={1}
              max={lastVerse}
              value={startText}
              onChange={e => setStartText(e.target.value)}
              onBlur={() => {
                const parsed = parseInt(startText, 10);
                const clamped = Number.isNaN(parsed) ? (startVerse || 1) : Math.min(Math.max(parsed, 1), lastVerse);
                setStartText(String(clamped));
                onSetVerseRange(clamped, endVerse);
              }}
              className="w-full px-3 py-2 bg-white/10 border border-white/15 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cerulean"
              inputMode="numeric"
              pattern="[0-9]*"
            />
          </div>
          <div className="flex-1 space-y-1">
            <label className="text-xs text-white/40 font-medium">End verse</label>
            <input
              type="number"
              min={startVerse || 1}
              max={lastVerse}
              value={endText}
              onChange={e => setEndText(e.target.value)}
              onBlur={() => {
                const parsed = parseInt(endText, 10);
                const clamped = Number.isNaN(parsed) ? (endVerse || lastVerse) : Math.min(Math.max(parsed, startVerse || 1), lastVerse);
                setEndText(String(clamped));
                onSetVerseRange(startVerse, clamped);
              }}
              className="w-full px-3 py-2 bg-white/10 border border-white/15 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cerulean"
              inputMode="numeric"
              pattern="[0-9]*"
            />
          </div>
          <div className="flex-shrink-0 space-y-1">
            <label className="text-xs text-white/40 font-medium">Quick</label>
            <button
              onClick={() => onSetVerseRange(1, lastVerse)}
              className="px-3 py-2 bg-white/10 text-white/70 text-xs rounded-lg hover:bg-white/20 active:scale-95 whitespace-nowrap"
            >
              All ({lastVerse})
            </button>
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full py-3 bg-gradient-to-r from-cerulean to-aqua-deep text-white rounded-xl font-semibold text-sm active:opacity-90 transition-opacity"
        >
          Select {selectedBookName} {selectedChapter}:{startVerse === endVerse ? startVerse : `${startVerse}–${endVerse}`}
        </button>
      </div>
      )}
    </div>
  );
};

export default PassageSelector;
